import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, Input, Checkbox } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
  getCurrentUserCampaigns,
  registerCampaign,
  getCampaignTeams,
} from '../../api/challenges';
import { backendUrl } from '../../api/http';

// Threshold used to label a team as "full" for main slots.
const MAIN_TEAM_MAX = 25;

// step: 'distance' → 'team' → done
export default function CampaignRegistrationPanel({
  campaign,
  campaignId,
  campaignName,
  mapLink,
  compact = false,
}) {
  const { user, isLoggedIn } = useAuth();
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [step, setStep] = useState('distance'); // 'distance' | 'team'
  const [chooseDistance, setDistance] = useState();
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null); // null = no team
  const [joinCode, setJoinCode] = useState('');
  const [asReserve, setAsReserve] = useState(false);
  const [registerStatus, setRegisterStatus] = useState('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let ignore = false;
    async function loadRegistrations() {
      const currentUserCampaigns = await getCurrentUserCampaigns(user);
      if (!ignore) setUserCampaigns(currentUserCampaigns);
    }
    loadRegistrations().catch((err) => setMessage(err.message));
    return () => { ignore = true; };
  }, [user]);

  const registeredCampaign = userCampaigns.find(
    (item) => String(item.campaign?.id) === String(campaignId)
  );
  const isRegistered = Boolean(registeredCampaign);
  const distances = campaign?.raceDistances || [];

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) || null;
  const isSelectedPrivate = selectedTeam?.visibility === 'private';
  const isSelectedMainFull = selectedTeam ? selectedTeam.member_count >= MAIN_TEAM_MAX : false;

  // ── Step 1: distance confirmed → load teams & advance ─────────────────────
  async function handleConfirmDistance() {
    if (!chooseDistance) {
      setMessage('Vui lòng chọn cự ly trước khi tiếp tục.');
      return;
    }
    setMessage('');
    setTeamsLoading(true);
    try {
      const data = await getCampaignTeams(campaignId);
      setTeams(data);
    } catch (_) {
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
    setStep('team');
  }

  // ── Step 2: confirm team & submit ─────────────────────────────────────────
  async function handleRegister() {
    // Validate: private team needs join_code
    if (isSelectedPrivate && !joinCode.trim()) {
      setMessage('Team này là riêng tư. Vui lòng nhập mã tham gia.');
      return;
    }

    setRegisterStatus('loading');
    setMessage('');
    try {
      const result = await registerCampaign({
        user,
        campaignId,
        distance: chooseDistance,
        teamId: selectedTeamId,
        joinCode: joinCode.trim() || null,
        asReserve,
      });
      if (result.status === 'success' || result.data) {
        setMessage(result.message || 'Đăng ký thành công.');
        setUserCampaigns([
          { campaign: { id: campaignId }, distance: chooseDistance },
          ...userCampaigns,
        ]);
      } else {
        setMessage(result.message || 'Không đăng ký được.');
      }
    } catch (err) {
      const code = err?.response?.data?.error?.details?.code
        || err?.response?.data?.data?.code;
      if (code === 'TEAM_FULL') {
        setMessage('Team này đã đủ thành viên chính. Bạn có thể đăng ký vị trí dự bị bên dưới.');
      } else if (code === 'INVALID_JOIN_CODE') {
        setMessage('Mã tham gia không đúng. Vui lòng kiểm tra lại.');
      } else {
        setMessage(err?.response?.data?.error?.message || err.message);
      }
    } finally {
      setRegisterStatus('idle');
    }
  }

  function handleSelectTeam(teamId) {
    setSelectedTeamId(teamId);
    setJoinCode('');
    setAsReserve(false);
    setMessage('');
  }

  function handleBack() {
    setStep('distance');
    setSelectedTeamId(null);
    setJoinCode('');
    setAsReserve(false);
    setMessage('');
  }

  return (
    <section
      className={
        compact
          ? 'campaign-register-panel campaign-register-panel-compact htv-card'
          : 'campaign-register-panel htv-card'
      }
    >
      <h2>Đăng ký miễn phí</h2>
      {campaignName && <p className="htv-muted">{campaignName}</p>}

      {isRegistered ? (
        <div className="campaign-action-row">
          <span className="htv-pill">
            Đã đăng ký{' '}
            {registeredCampaign.distance || registeredCampaign.distance_register || ''} km
          </span>
          {mapLink && (
            <Link className="ant-btn ant-btn-primary" to={mapLink}>
              Mở bản đồ hành trình
            </Link>
          )}
        </div>
      ) : step === 'distance' ? (
        /* ── Step 1: chọn cự ly ── */
        <div className="campaign-action-row">
          <Select
            placeholder="Chọn cự ly"
            style={{ minWidth: 160 }}
            onChange={setDistance}
            value={chooseDistance}
            options={distances.map((distance) => ({
              value: distance,
              label: `${distance} km`,
            }))}
          />
          <Button type="primary" onClick={handleConfirmDistance}>
            Tiếp theo →
          </Button>
          {!isLoggedIn && (
            <a className="htv-auth-link" href={`${backendUrl}/api/connect/strava`}>
              Đăng nhập Strava
            </a>
          )}
        </div>
      ) : (
        /* ── Step 2: chọn team ── */
        <div className="campaign-register-team-step">
          <p className="htv-muted" style={{ marginBottom: 8 }}>
            Cự ly: <strong>{chooseDistance} km</strong> —{' '}
            <button
              className="htv-link-btn"
              onClick={handleBack}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Đổi
            </button>
          </p>

          <p style={{ fontWeight: 600, marginBottom: 8 }}>Chọn team (không bắt buộc)</p>

          {teamsLoading ? (
            <p className="htv-muted">Đang tải danh sách team...</p>
          ) : (
            <div
              className="campaign-team-list"
              style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}
            >
              {/* No-team option */}
              <TeamOption
                selected={selectedTeamId === null}
                onSelect={() => handleSelectTeam(null)}
                label="Không tham gia team nào"
              />

              {teams.map((team) => {
                const mainFull = team.member_count >= MAIN_TEAM_MAX;
                return (
                  <TeamOption
                    key={team.id}
                    team={team}
                    selected={selectedTeamId === team.id}
                    onSelect={() => handleSelectTeam(team.id)}
                    mainFull={mainFull}
                  />
                );
              })}

              {teams.length === 0 && (
                <p className="htv-muted" style={{ fontSize: 13 }}>
                  Campaign chưa có team nào. Bạn sẽ đăng ký không thuộc team.
                </p>
              )}
            </div>
          )}

          {/* Private team: join_code input */}
          {isSelectedPrivate && (
            <div style={{ marginBottom: 12 }}>
              <Input
                prefix={<LockOutlined />}
                placeholder="Nhập mã tham gia (join code)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                style={{ maxWidth: 320 }}
                allowClear
              />
              <p className="htv-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Team này là riêng tư. Liên hệ đội trưởng để lấy mã tham gia.
              </p>
            </div>
          )}

          {/* Reserve slot option: show when main slots are full */}
          {isSelectedMainFull && selectedTeamId !== null && (
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                checked={asReserve}
                onChange={(e) => setAsReserve(e.target.checked)}
              >
                Đăng ký vị trí <strong>dự bị</strong> (main đã đầy)
              </Checkbox>
              <p className="htv-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Thành viên dự bị có thể được nâng lên main nếu có người rút lui.
              </p>
            </div>
          )}

          <div className="campaign-action-row">
            <Button onClick={handleBack} style={{ marginRight: 8 }}>
              ← Quay lại
            </Button>
            <Button
              type="primary"
              loading={registerStatus === 'loading'}
              onClick={handleRegister}
            >
              {isLoggedIn ? 'Xác nhận đăng ký' : 'Đăng ký demo'}
            </Button>
            {!isLoggedIn && (
              <a className="htv-auth-link" href={`${backendUrl}/api/connect/strava`}>
                Đăng nhập Strava
              </a>
            )}
          </div>
        </div>
      )}

      {message && (
        <p
          className="htv-muted"
          style={{
            marginTop: 8,
            color: message.includes('thành công') ? '#52c41a' : '#cf1322',
          }}
        >
          {message}
        </p>
      )}
    </section>
  );
}

// ── TeamOption sub-component ──────────────────────────────────────────────────

function TeamOption({ team, selected, onSelect, mainFull, label }) {
  if (!team) {
    // "no team" row
    return (
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: 8,
          border: `2px solid ${selected ? '#1677ff' : '#e8e8e8'}`,
          background: selected ? '#e6f4ff' : '#fff',
        }}
      >
        <input
          type="radio"
          name="team"
          checked={selected}
          onChange={onSelect}
          style={{ accentColor: '#1677ff' }}
        />
        <span>{label}</span>
      </label>
    );
  }

  const isPrivate = team.visibility === 'private';

  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: mainFull && !selected ? 'default' : 'pointer',
        padding: '8px 12px',
        borderRadius: 8,
        border: `2px solid ${selected ? '#1677ff' : '#e8e8e8'}`,
        background: selected ? '#e6f4ff' : '#fff',
      }}
    >
      <input
        type="radio"
        name="team"
        value={team.id}
        checked={selected}
        onChange={onSelect}
        style={{ accentColor: '#1677ff' }}
      />
      {team.logo_url && (
        <img
          src={team.logo_url}
          alt=""
          style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }}
        />
      )}
      <span style={{ flex: 1 }}>
        {team.name}
        {isPrivate && (
          <LockOutlined
            style={{ marginLeft: 6, fontSize: 12, color: '#8c8c8c' }}
            title="Team riêng tư"
          />
        )}
      </span>
      {team.slogan && (
        <span className="htv-muted" style={{ fontSize: 12 }}>
          {team.slogan}
        </span>
      )}
      <span
        style={{
          fontSize: 12,
          color: mainFull ? '#cf1322' : '#52c41a',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {mainFull ? 'Main đầy' : `${team.member_count}/${MAIN_TEAM_MAX}`}
      </span>
    </label>
  );
}
